import { motion } from "framer-motion";
import { Users, Star, Monitor, Clock } from "lucide-react";
import { SEO } from "@/components/shared";
import { fadeInUp, staggerContainer, viewportOnce, HERO_IMAGES } from "@/constants";
import { PHOTOS, PhotoMosaic } from "@/components/Photo";

const EVENT_MOSAIC = [
  { src: PHOTOS.conference, label: "Expert keynotes" },
  { src: PHOTOS.workshop, label: "Corporate workshops" },
  { src: PHOTOS.lecture, label: "Faculty sessions" },
  { src: PHOTOS.labTeacher, label: "Hands-on labs" },
  { src: PHOTOS.team, label: "Leadership roundtables" },
];

export default function EventsPage() {
  return (
    <div className="pt-24">
      <SEO
        title="Events"
        description="Upcoming events, webinars and workshops by Qbic Catalyst Partners. Join our sessions on AI training, Intel Unnati, and enterprise consulting."
        keywords="Qbic Catalyst events, webinars, AI workshops, Intel Unnati webinar, consulting events"
        path="/events"
      />
      {/* Hero */}
      <section className="section page-hero" style={{ "--hero-img": `url(${HERO_IMAGES.events})` }}>
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={staggerContainer} className="max-w-4xl">
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
              Events & Webinars
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">
              Upcoming <span className="text-gradient">Events</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-[#A0ABC0]">
              Join our live sessions to learn from industry experts and explore opportunities for growth.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Event Card */}
      <section className="section theme-light tint-ivory">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            className="bg-[#0A1628] rounded-2xl border border-[#2A3648] overflow-hidden"
          >
            <div className="grid lg:grid-cols-5 gap-0">
              <div className="lg:col-span-1 bg-gradient-to-br from-[#C9A962]/15 to-[#C9A962]/5 p-8 flex flex-col items-center justify-center border-b lg:border-b-0 lg:border-r border-[#2A3648]">
                <p className="text-[#C9A962] text-sm uppercase tracking-wider font-semibold mb-1">May</p>
                <p className="text-5xl font-bold text-white mb-1">09</p>
                <p className="text-[#A0ABC0] text-sm">2026</p>
                <div className="mt-4 flex items-center gap-1.5 text-[#A0ABC0] text-xs">
                  <Clock size={12} className="text-[#C9A962]" />
                  <span>11:00 AM IST</span>
                </div>
              </div>
              <div className="lg:col-span-4 p-8 md:p-10">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 bg-[#C9A962]/10 border border-[#C9A962]/25 rounded-full px-3 py-1 text-[#C9A962] text-xs font-semibold uppercase tracking-wider">
                    <Monitor size={12} /> Live Webinar
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-[#0071C5]/10 border border-[#0071C5]/25 rounded-full px-3 py-1 text-[#0071C5] text-xs font-semibold">
                    Intel Partnership
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  Intel&#174; Unnati &#8212; Program Briefing
                </h2>
                <p className="text-[#A0ABC0] mb-6 max-w-2xl">
                  An exclusive session for Deans, Principals, HODs, and Professors to discover how Intel Unnati can bring world-class AI infrastructure, faculty development, and industry-aligned certifications to their institutions.
                </p>
                <div className="flex flex-wrap items-center gap-6 mb-6 text-sm text-[#A0ABC0]">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-[#C9A962]" />
                    <span>For Higher Education Leaders</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Monitor size={16} className="text-[#C9A962]" />
                    <span>Online</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star size={16} className="text-[#C9A962]" />
                    <span>Free Registration</span>
                  </div>
                </div>
                <span
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2A3648] text-[#A0ABC0] rounded-lg text-sm font-medium cursor-default"
                  data-testid="events-webinar-cta"
                >
                  Webinar Completed
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Past session moments */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">From Our Sessions</motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold">Workshops, webinars & expert talks</motion.h2>
            </div>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] max-w-md" data-testid="events-coming-soon">
              More events coming soon &mdash; stay tuned for upcoming workshops, training sessions, and industry meetups.
            </motion.p>
          </motion.div>
          <PhotoMosaic items={EVENT_MOSAIC} testId="events-mosaic" />
        </div>
      </section>
    </div>
  );
}
